import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  additionalAmtWithheldQuestion,
  basicInfoQuestions,
  dependentsQuestion,
  exemptionQuestions_12_20,
  exemptionQuestions_12_22,
  worksheetMultSelectQuestion,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import StateTaxFormLayout from '../common/state-tax-content-layout';

const getExemptionQuestions = (version = '') => {
  switch (version) {
    case '12_22':
    case '12_23':
      return exemptionQuestions_12_22;
    default:
      return exemptionQuestions_12_20;
  }
};

function LegacyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(
    data.is_exempt || false,
  );

  const worksheetForm = useRef();
  const [worksheetOpen, setWorksheetOpen] = useState(false);

  const [allowancesClaimed, setAllowancesClaimed] = useState(
    data.num_allowances_claimed || '',
  );
  const [allowancesClaimedError, setAllowancesClaimedError] = useState(false);
  const [allowancesClaimedValueError, setAllowancesClaimedValueError] = useState(false);

  const worksheetMultSelectInitQ = getInitialQuestions(
    worksheetMultSelectQuestion,
  );
  const [worksheetMultSelect, setWorksheetMultSelect] = useState(
    worksheetMultSelectInitQ,
  );

  const dependentsForm = useRef();
  const dependentsInitQ = getInitialQuestions(dependentsQuestion);
  const [dependents, setDependents] = useState(dependentsInitQ);

  const additionalAmtInitQ = getInitialQuestions(additionalAmtWithheldQuestion);
  const [additionalAmtWithheld, setAdditionalAmtWithheld] = useState(additionalAmtInitQ);

  const exemptionsForm = useRef();
  const exemptionQuestions = getExemptionQuestions(version);
  const exemptionInitQ = getInitialQuestions(exemptionQuestions);
  const [exemptionStatus, setExemptionStatus] = useState(exemptionInitQ);

  useEffect(() => {
    setExemptionStatus(exemptionInitQ);
  }, [version]);

  const [isTribalMember, setIsTribalMember] = useState(
    data.is_tribal_member || false,
  );

  const renderBasicInfoForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const [totalAllowances, setTotalAllowances] = useState(
    data.total_num_allowances || 0,
  );
  const calculateTotalAllowances = () => {
    const dependentAllowances = worksheetMultSelect[0].answer.answer.applicable_allowances.length || 0;
    const numDependents = parseInt(dependents[0].answer.answer.dependents) || 0;
    const value = numDependents + dependentAllowances;
    return value;
  };

  const renderWorksheetQuestions = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={worksheetMultSelect}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = worksheetMultSelect.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWorksheetMultSelect(questions);
        }}
      />
      <FormConsumer
        questions={dependents}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = dependents.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setDependents(questions);
        }}
        ref={dependentsForm}
      />
      <S.QuestionLabel mb="7px">Total allowances</S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={
                worksheetOpen ? calculateTotalAllowances() : totalAllowances
              }
            disabled
          />
        </Flex>
      </S.QuestionWrapper>
      <FormConsumer
        questions={additionalAmtWithheld}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalAmtWithheld.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalAmtWithheld(questions);
        }}
        ref={worksheetForm}
      />
    </Flex>
  );

  const renderDropdown = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="7px">
        Total number of allowances you are claiming on the Personal Allowances Worksheet (Line 4)
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={allowancesClaimed}
            onChange={(e) => setAllowancesClaimed(e.target.value)}
            disabled={isReviewStep}
            style={{
              borderColor:
                  allowancesClaimedValueError || allowancesClaimedError
                    ? 'red'
                    : '',
            }}
          />
        </Flex>
      </S.QuestionWrapper>
      {allowancesClaimedValueError && (
      <Flex justifyContent="flex-start">
        <Text color="red">Please enter a valid response.</Text>
      </Flex>
      )}
      {allowancesClaimedError && (
      <Flex justifyContent="flex-start">
        <Text color="red">
          You may not claim more allowances than you are entitled to unless
          you file a Personal Withholding Allowance Variance Certificate to
          obtain permission from the State Tax Assessor.
        </Text>
      </Flex>
      )}
      <Flex mt="10px">
        <CollapsibleQuestions
          title="If you need to calculate your total number of allowances, use the worksheet."
          handleClick={() => {
            if (!isReviewStep) {
              setWorksheetOpen(!worksheetOpen);
            }
          }}
          open={worksheetOpen || isReviewStep}
        >
          {!isExemptFromWithholding && renderWorksheetQuestions(isReviewStep)}
        </CollapsibleQuestions>
      </Flex>
    </Flex>
  );

  const renderExemptForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={exemptionStatus}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = exemptionStatus.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setExemptionStatus(questions);
        }}
        ref={exemptionsForm}
      />
    </Flex>
  );

  const renderTribalExemption = ({ isReviewStep = false, version = '' }) => {
    if (!(['12_22', '12_23'].includes(version))) return null;

    return (
      <Flex column>
        <S.QuestionLabel>
          Check this box if you are an enrolled tribal member residing on tribal
          land in Maine and you are claiming an exemption from Maine withholding
          on the wages, salaries, or other compensation derived from or
          connected with sources on tribal land in Maine (that is, wages,
          salaries, or other compensation
          {' '}
          <strong>for work performed on tribal land in Maine</strong>
          ).
        </S.QuestionLabel>
        <Flex mb="20px">
          <S.Input
            type="checkbox"
            checked={isTribalMember}
            disabled={isReviewStep}
            onChange={() => {
              setIsTribalMember(!isTribalMember);
            }}
          />
        </Flex>
      </Flex>
    );
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {!isExemptFromWithholding && renderDropdown()}
          {isExemptFromWithholding && renderExemptForm()}
          {renderTribalExemption({ version })}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (worksheetForm && worksheetForm.current) {
              worksheetForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (exemptionsForm && exemptionsForm.current) {
              exemptionsForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (dependentsForm && dependentsForm.current) {
              dependentsForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowancesClaimed > calculateTotalAllowances()) {
              err = true;
              setAllowancesClaimedError(true);
            } else {
              setAllowancesClaimedError(false);
            }
            if (!/^\d*$/.test(allowancesClaimed)) {
              err = true;
              setAllowancesClaimedValueError(true);
            } else {
              setAllowancesClaimedValueError(false);
            }
            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step

              const basicInfoData = basicInfo.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const exemptionData = { is_exempt: isExemptFromWithholding };

              let exemptionQuestionData;
              let wsMultSelectData;
              let dependentsData;
              let additionalAmtWithheldData;

              let allowancesClaimedTemp = '';
              let totalAllowancesTemp = '';

              if (isExemptFromWithholding) {
                exemptionQuestionData = exemptionStatus.reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );

                wsMultSelectData = resetInitialQuestions(
                  worksheetMultSelectQuestion,
                ).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                dependentsData = resetInitialQuestions(
                  dependentsQuestion,
                ).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                additionalAmtWithheldData = resetInitialQuestions(
                  additionalAmtWithheldQuestion,
                ).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              } else {
                exemptionQuestionData = resetInitialQuestions(
                  exemptionQuestions,
                ).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                wsMultSelectData = worksheetMultSelect.reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );
                dependentsData = dependents.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                additionalAmtWithheldData = additionalAmtWithheld.reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );
                allowancesClaimedTemp = allowancesClaimed;
                totalAllowancesTemp = calculateTotalAllowances();
              }

              const allowancesData = {
                num_allowances_claimed: allowancesClaimedTemp.toString(),
                total_num_allowances: totalAllowancesTemp.toString(),
              };
              const worksheetData = {
                ...wsMultSelectData,
                ...dependentsData,
                ...additionalAmtWithheldData,
              };
              const tribalMemberData = {
                is_tribal_member: isTribalMember,
              };
              const data = {
                ...basicInfoData,
                ...exemptionData,
                ...allowancesData,
                ...worksheetData,
                ...exemptionQuestionData,
                ...tribalMemberData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {!isExemptFromWithholding && renderDropdown(true)}
        {isExemptFromWithholding && renderExemptForm(true)}
        {renderTribalExemption({ version, isReviewStep: true })}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default LegacyForm;
